import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Calendar, Globe, Star, MapPin, Tag, BookOpen, Gift, Moon } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ApiService from '../../services/api';
import CloudinaryService from '../../services/cloudinary';

const AutoResizeTextarea = ({ name, value, onChange, placeholder, rows = 3, className, required }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      name={name}
      value={value}
      onChange={onChange}
      rows={rows}
      required={required}
      className={`${className} overflow-hidden resize-none`}
      placeholder={placeholder}
    />
  );
};

const AddEditFestivalModal = ({ isOpen, onClose, onSave, festival, isEditing }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: 'https://res.cloudinary.com/ddkmjgxk4/image/upload/v1771585962/vaidic/festivals/nep1ntznpru8fsoklkiw.jpg',
    category: 'Religious',
    priority: 0,
    isActive: true,
    startDate: '',
    endDate: '',
    isRecurring: false,
    significance: '',
    rituals: [],
    offerings: [],
    regions: [],
    tithi: '',
    nakshatra: '',
    paksha: '',
    mahina: ''
  });

  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [errors, setErrors] = useState({});

  // Additional state for array inputs
  const [ritualInput, setRitualInput] = useState('');
  const [offeringInput, setOfferingInput] = useState('');
  const [regionInput, setRegionInput] = useState('');

  const categories = ['Religious', 'Cultural', 'Seasonal', 'Regional', 'Traditional', 'Spiritual', 'Other'];

  const tithiOptions = [
    'Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami',
    'Shashthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami',
    'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima', 'Amavasya'
  ];

  const nakshatraOptions = [
    'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
    'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
    'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
    'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishtha',
    'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
  ];

  const pakshaOptions = ['Shukla Paksha', 'Krishna Paksha'];

  const mahinaOptions = [
    'Chaitra', 'Vaishakha', 'Jyeshtha', 'Ashadha', 'Shravana', 'Bhadrapada',
    'Ashwin', 'Kartik', 'Margashirsha', 'Paush', 'Magha', 'Phalguna'
  ];

  const modules = {
      toolbar: [
          [{ 'header': [1, 2, false] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
          ['link', 'image'],
          ['clean']
      ],
  };

  const formats = [
      'header',
      'bold', 'italic', 'underline', 'strike', 'blockquote',
      'list', 'bullet', 'indent',
      'link', 'image'
  ];

  useEffect(() => {
    if (isOpen) {
      if (isEditing && festival) {
        setFormData({
          name: festival.name || '',
          description: festival.description || '',
          imageUrl: festival.imageUrl || '',
          category: festival.category || 'Religious',
          priority: festival.priority || 0,
          isActive: festival.isActive !== undefined ? festival.isActive : true,
          startDate: festival.startDate ? new Date(festival.startDate).toISOString().split('T')[0] : '',
          endDate: festival.endDate ? new Date(festival.endDate).toISOString().split('T')[0] : '',
          isRecurring: festival.isRecurring || false,
          significance: festival.significance || '',
          rituals: festival.rituals || [],
          offerings: festival.offerings || [],
          regions: festival.regions || [],
          tithi: festival.tithi || '',
          nakshatra: festival.nakshatra || '',
          paksha: festival.paksha || '',
          mahina: festival.mahina || ''
        });
        setPreviewImage(festival.imageUrl || '');
      } else {
        // Reset form for new festival
        setFormData({
          name: '',
          description: '',
          imageUrl: 'https://res.cloudinary.com/ddkmjgxk4/image/upload/v1771585962/vaidic/festivals/nep1ntznpru8fsoklkiw.jpg',
          category: 'Religious',
          priority: 0,
          isActive: true,
          startDate: '',
          endDate: '',
          isRecurring: false,
          significance: '',
          rituals: [],
          offerings: [],
          regions: [],
          tithi: '',
          nakshatra: '',
          paksha: '',
          mahina: ''
        });
        setPreviewImage('');
      }
      setImageFile(null);
      setErrors({});
      setRitualInput('');
      setOfferingInput('');
      setRegionInput('');
    }
  }, [isOpen, isEditing, festival]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validation = CloudinaryService.validateFile(file, {
        maxSize: 5 * 1024 * 1024, // 5MB
        allowedTypes: ['.jpg', '.jpeg', '.png', '.webp']
      });

      if (!validation.isValid) {
        setErrors(prev => ({ ...prev, image: validation.errors.join(', ') }));
        return;
      }

      setImageFile(file);
      setErrors(prev => ({ ...prev, image: '' }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setPreviewImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.imageUrl;

    setImageUploading(true);
    try {
      const result = await CloudinaryService.uploadFile(imageFile, {
        folder: 'vaidic/festivals',
        tags: ['festival', 'admin-upload'],
      });

      if (result.success) {
        return result.url;
      } else {
        throw new Error(result.error || 'Image upload failed');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      setErrors(prev => ({ ...prev, image: error.message }));
      return formData.imageUrl;
    } finally {
      setImageUploading(false);
    }
  };

  const addToArray = (arrayName, inputValue, setInputValue) => {
    if (inputValue.trim() && !formData[arrayName].includes(inputValue.trim())) {
      setFormData(prev => ({
        ...prev,
        [arrayName]: [...prev[arrayName], inputValue.trim()]
      }));
      setInputValue('');
    }
  };

  const removeFromArray = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Festival name is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (formData.endDate && formData.endDate <= formData.startDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    // Validate significance field - check if it's empty or contains only HTML tags
    if (!formData.significance || formData.significance.trim() === '' || 
        formData.significance.replace(/<[^>]*>/g, '').trim() === '') {
      newErrors.significance = 'Festival significance is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Upload image if new image selected
      const imageUrl = await uploadImage();

      const festivalData = {
        ...formData,
        imageUrl,
        priority: parseInt(formData.priority) || 0,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
      };

      let response;
      if (isEditing) {
        response = await ApiService.updateFestival(festival.id, festivalData);
      } else {
        response = await ApiService.createFestival(festivalData);
      }

      if (response.success) {
        onSave();
      } else {
        setErrors({ submit: response.message || 'An error occurred' });
      }
    } catch (error) {
      console.error('Error saving festival:', error);
      
      // Handle different types of errors
      const newErrors = {};
      
      if (error.response && error.response.details) {
        // If the backend provides detailed validation errors, display them specifically
        const details = error.response.details;
        if (typeof details === 'string') {
          // Parse string details like "\"significance\" is not allowed to be empty"
          if (details.includes('significance')) {
            newErrors.significance = 'Festival significance is required';
          } else if (details.includes('name')) {
            newErrors.name = 'Festival name is required';
          } else if (details.includes('startDate')) {
            newErrors.startDate = 'Start date is required';
          } else {
            newErrors.submit = details;
          }
        } else if (typeof details === 'object') {
          // Handle object-based validation errors
          Object.keys(details).forEach(field => {
            if (field === 'significance') {
              newErrors.significance = 'Festival significance is required';
            } else if (field === 'name') {
              newErrors.name = details[field];
            } else if (field === 'startDate') {
              newErrors.startDate = details[field];
            } else {
              newErrors[field] = details[field];
            }
          });
        }
      } else {
        // Fallback to generic error message
        newErrors.submit = error.message || 'An error occurred while saving the festival';
      }
      
      setErrors(newErrors);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div translate="no" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">
            <span>{isEditing ? 'Edit Festival' : 'Add New Festival'}</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Basic Information
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Festival Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter festival name"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 [&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!border-b [&_.ql-toolbar]:!border-gray-200 [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:!border-0 [&_.ql-container]:rounded-b-lg [&_.ql-container]:h-48 [&_.ql-editor]:overflow-y-auto">
                        <ReactQuill
                            theme="snow"
                            value={formData.description || ''}
                            onChange={(content) => {
                                setFormData(prev => ({ ...prev, description: content }));
                                if (errors.description) setErrors(prev => ({ ...prev, description: '' }));
                            }}
                            modules={modules}
                            formats={formats}
                            className="h-full"
                        />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        {categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>x

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority (0-10)
                      </label>
                      <input
                        type="number"
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        min="0"
                        max="10"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isRecurring"
                        checked={formData.isRecurring}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Recurring Festival</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Festival Dates
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date (Optional)
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    {errors.endDate && <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Festival Image */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Festival Image
                </h3>

                <div className="space-y-4">
                  {previewImage && (
                    <div className="relative">
                      <img
                        src={previewImage}
                        alt="Festival preview"
                        className="w-full h-48 object-cover rounded-lg border border-gray-200"
                      />
                    </div>
                  )}

                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      id="festivalImage"
                    />
                    <label
                      htmlFor="festivalImage"
                      className="w-full flex items-center justify-center px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-400 cursor-pointer transition-colors"
                    >
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">
                          <span>{imageFile ? imageFile.name : 'Click to upload festival image'}</span>
                        </p>
                        <p className="text-xs text-gray-400"><span>PNG, JPG, WEBP up to 5MB</span></p>
                      </div>
                    </label>
                    {errors.image && <p className="text-red-500 text-sm mt-1">{errors.image}</p>}
                  </div>
                </div>
              </div>

              {/* Panchang Details */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Moon className="w-5 h-5" />
                  Panchang Details
                  <span className="text-sm font-normal text-gray-400">(Optional)</span>
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tithi (Lunar Day)
                    </label>
                    <select
                      name="tithi"
                      value={formData.tithi}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">-- Select Tithi --</option>
                      {tithiOptions.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Paksha (Fortnight)
                    </label>
                    <select
                      name="paksha"
                      value={formData.paksha}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">-- Select Paksha --</option>
                      {pakshaOptions.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mahina (Hindu Month)
                    </label>
                    <select
                      name="mahina"
                      value={formData.mahina}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">-- Select Mahina --</option>
                      {mahinaOptions.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nakshatra (Lunar Mansion)
                    </label>
                    <select
                      name="nakshatra"
                      value={formData.nakshatra}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">-- Select Nakshatra --</option>
                      {nakshatraOptions.map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Cultural Significance */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Cultural Information
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Significance & Story *
                    </label>
                    <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 [&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!border-b [&_.ql-toolbar]:!border-gray-200 [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:!border-0 [&_.ql-container]:rounded-b-lg [&_.ql-container]:h-48 [&_.ql-editor]:overflow-y-auto">
                        <ReactQuill
                            theme="snow"
                            value={formData.significance || ''}
                            onChange={(content) => {
                                setFormData(prev => ({ ...prev, significance: content }));
                                if (errors.significance) setErrors(prev => ({ ...prev, significance: '' }));
                            }}
                            modules={modules}
                            formats={formats}
                            className="h-full"
                        />
                    </div>
                    {errors.significance && <p className="text-red-500 text-sm mt-1">{errors.significance}</p>}
                  </div>

                  {/* Rituals */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rituals & Practices
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={ritualInput}
                        onChange={(e) => setRitualInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('rituals', ritualInput, setRitualInput))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Add ritual or practice"
                      />
                      <button
                        type="button"
                        onClick={() => addToArray('rituals', ritualInput, setRitualInput)}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.rituals.map((ritual, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                        >
                          {ritual}
                          <button
                            type="button"
                            onClick={() => removeFromArray('rituals', index)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Offerings */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Offerings & Prasad
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={offeringInput}
                        onChange={(e) => setOfferingInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('offerings', offeringInput, setOfferingInput))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Add offering or prasad"
                      />
                      <button
                        type="button"
                        onClick={() => addToArray('offerings', offeringInput, setOfferingInput)}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.offerings.map((offering, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-sm rounded-full"
                        >
                          {offering}
                          <button
                            type="button"
                            onClick={() => removeFromArray('offerings', index)}
                            className="text-green-600 hover:text-green-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Regions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Regions Celebrated
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={regionInput}
                        onChange={(e) => setRegionInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('regions', regionInput, setRegionInput))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Add region or state"
                      />
                      <button
                        type="button"
                        onClick={() => addToArray('regions', regionInput, setRegionInput)}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.regions.map((region, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-sm rounded-full"
                        >
                          {region}
                          <button
                            type="button"
                            onClick={() => removeFromArray('regions', index)}
                            className="text-purple-600 hover:text-purple-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <span>Cancel</span>
            </button>
            <button
              type="submit"
              disabled={loading || imageUploading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{imageUploading ? 'Uploading Image...' : 'Saving...'}</span>
                </>
              ) : (
                <>
                  <Star className="w-4 h-4" />
                  <span>{isEditing ? 'Update Festival' : 'Create Festival'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditFestivalModal;